import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { useClientLicenses } from "@/hooks/useClientLicenses";
import { useBillingOperations } from "@/hooks/useBillingOperations";

interface GenerateInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const GenerateInvoiceDialog = ({ open, onOpenChange }: GenerateInvoiceDialogProps) => {
  const [clientLicenseId, setClientLicenseId] = useState("");
  const [amount, setAmount] = useState("");
  const [billingPeriodStart, setBillingPeriodStart] = useState<Date>();
  const [billingPeriodEnd, setBillingPeriodEnd] = useState<Date>();
  const [dueDate, setDueDate] = useState<Date>();
  const [notes, setNotes] = useState("");

  const { clientLicenses } = useClientLicenses();
  const { generateInvoice, isGeneratingInvoice } = useBillingOperations();

  const activeLicenses = clientLicenses.filter(license => license.status === 'active');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!clientLicenseId || !amount || !billingPeriodStart || !billingPeriodEnd || !dueDate) {
      return;
    }

    generateInvoice({
      clientLicenseId,
      amount: parseFloat(amount),
      billingPeriodStart: billingPeriodStart.toISOString(),
      billingPeriodEnd: billingPeriodEnd.toISOString(),
      dueDate: dueDate.toISOString(),
      notes: notes || undefined,
    });

    // Reset form
    setClientLicenseId("");
    setAmount("");
    setBillingPeriodStart(undefined);
    setBillingPeriodEnd(undefined);
    setDueDate(undefined);
    setNotes("");
    onOpenChange(false);
  };

  const selectedLicense = activeLicenses.find(license => license.id === clientLicenseId);

  // Auto-fill amount when license is selected
  React.useEffect(() => {
    if (selectedLicense?.license_plan) {
      const licenseAmount = selectedLicense.billing_cycle === 'yearly' 
        ? selectedLicense.license_plan.price_yearly || selectedLicense.license_plan.price_monthly * 12
        : selectedLicense.license_plan.price_monthly;
      setAmount(licenseAmount.toString());
    }
  }, [selectedLicense]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Gerar Fatura Manual</DialogTitle>
          <DialogDescription>
            Gere uma fatura manual para um cliente específico
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="client-license">Cliente/Licença</Label>
            <Select value={clientLicenseId} onValueChange={setClientLicenseId}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione uma licença ativa" />
              </SelectTrigger>
              <SelectContent>
                {activeLicenses.map((license) => (
                  <SelectItem key={license.id} value={license.id}>
                    {license.client?.name} - {license.license_plan?.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="amount">Valor (R$)</Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0,00"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Período - Início</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !billingPeriodStart && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {billingPeriodStart ? (
                      format(billingPeriodStart, "dd/MM/yyyy", { locale: ptBR })
                    ) : (
                      "Selecionar"
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <CalendarComponent
                    mode="single"
                    selected={billingPeriodStart}
                    onSelect={setBillingPeriodStart}
                    locale={ptBR}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Período - Fim</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !billingPeriodEnd && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {billingPeriodEnd ? (
                      format(billingPeriodEnd, "dd/MM/yyyy", { locale: ptBR })
                    ) : (
                      "Selecionar"
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <CalendarComponent
                    mode="single"
                    selected={billingPeriodEnd}
                    onSelect={setBillingPeriodEnd}
                    locale={ptBR}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Data de Vencimento</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !dueDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dueDate ? (
                    format(dueDate, "dd/MM/yyyy", { locale: ptBR })
                  ) : (
                    "Selecionar data de vencimento"
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <CalendarComponent
                  mode="single"
                  selected={dueDate}
                  onSelect={setDueDate}
                  locale={ptBR}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Observações (opcional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Observações sobre esta fatura..."
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isGeneratingInvoice}>
              {isGeneratingInvoice ? "Gerando..." : "Gerar Fatura"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};